import type { PublicShareManifest, SnapshotItem } from '@containd/shared';
import {
  parseCanvasParentFromPathname,
  shareCanvasPath,
  shareItemPathWithFocus,
  type SeoShareManifest,
} from '@containd/shared';

/** One canvas visit. History may include lateral placeholder jumps (not only nest ancestry). */
export type ShareVisit = {
  canvasUuid: string;
  focusItemUuid?: string | null;
};

function asSeoManifest(manifest: PublicShareManifest): SeoShareManifest {
  return manifest as unknown as SeoShareManifest;
}

/** Path (+ optional `?item=`) for the current visit — same contract as SEO nested URLs. */
export function pathForShareVisit(
  manifest: PublicShareManifest,
  visit: ShareVisit,
): string {
  const seo = asSeoManifest(manifest);
  const focus = visit.focusItemUuid?.trim();
  if (focus) {
    const item = manifest.items.find((i) => i.uuid === focus);
    if (item) return shareItemPathWithFocus(seo, item);
  }
  return shareCanvasPath(seo, visit.canvasUuid);
}

export function focusItemUuidFromSearch(search: string): string | null {
  const raw = String(search || '');
  const q = raw.startsWith('?') ? raw.slice(1) : raw;
  const item = new URLSearchParams(q).get('item')?.trim();
  return item || null;
}

/**
 * Rebuild the nest ancestry stack from a `/s/...` or `/share/...` location.
 * Floor is always share root; unknown segments fall back to root.
 */
export function stackFromShareLocation(
  manifest: PublicShareManifest,
  pathname: string,
  search = '',
): ShareVisit[] {
  const seo = asSeoManifest(manifest);
  const parent =
    parseCanvasParentFromPathname(pathname, seo) ?? shareRootUuid(manifest);
  const visits = visitsFromAncestry(buildShareCanvasStack(manifest, parent));
  const focus = focusItemUuidFromSearch(search);
  if (focus && visits.length > 0) {
    const last = visits[visits.length - 1]!;
    visits[visits.length - 1] = { ...last, focusItemUuid: focus };
  }
  return visits;
}

/** Compare path+search ignoring trailing slash on the pathname. */
export function shareLocationEquals(a: string, b: string): boolean {
  const norm = (raw: string) => {
    const [pathPart, query = ''] = String(raw || '').split('?');
    const path = pathPart.replace(/\/+$/, '') || '/';
    return query ? `${path}?${query}` : path;
  };
  return norm(a) === norm(b);
}

export function visitsFromAncestry(canvasUuids: string[]): ShareVisit[] {
  return canvasUuids.map((canvasUuid) => ({ canvasUuid }));
}

/**
 * Share root (= published item / freeze `rootItemUuid`) is the navigation floor.
 * The outer canvas (parentUuid null, single root tile) is not a viewer level —
 * that tile is preview/search only, not an openable item path.
 */
export function shareRootUuid(manifest: PublicShareManifest): string {
  return manifest.rootItemUuid;
}

export function isShareRootItem(
  manifest: PublicShareManifest,
  itemUuid: string | null | undefined,
): boolean {
  return Boolean(itemUuid) && itemUuid === manifest.rootItemUuid;
}

/**
 * Build canvas nest stack. Always starts at rootItemUuid (inside the list).
 * `initialParentUuid` may be a nested container under root; null/undefined/root → root only.
 */
export function buildShareCanvasStack(
  manifest: PublicShareManifest,
  initialParentUuid?: string | null,
): string[] {
  const root = shareRootUuid(manifest);
  if (
    initialParentUuid == null ||
    initialParentUuid === '' ||
    initialParentUuid === root
  ) {
    return [root];
  }

  const byUuid = new Map(manifest.items.map((i) => [i.uuid, i]));
  if (!byUuid.has(initialParentUuid)) {
    return [root];
  }

  const chain: string[] = [];
  let cur: string | null | undefined = initialParentUuid;
  const guard = new Set<string>();
  while (cur && cur !== root) {
    if (guard.has(cur)) break;
    guard.add(cur);
    chain.unshift(cur);
    cur = byUuid.get(cur)?.parentUuid;
  }
  return [root, ...chain];
}

export function sharePathSegments(
  manifest: PublicShareManifest,
  stack: string[],
): Array<{ uuid: string; title: string }> {
  const root = shareRootUuid(manifest);
  const segs: Array<{ uuid: string; title: string }> = [
    { uuid: root, title: manifest.title },
  ];
  for (let i = 1; i < stack.length; i++) {
    const uuid = stack[i]!;
    if (uuid === root) continue;
    const item = manifest.items.find((x) => x.uuid === uuid);
    segs.push({ uuid, title: item?.title ?? uuid });
  }
  return segs;
}

/**
 * Canvas whose interior contains this item. Share root tile is not a viewer level.
 * Missing items / unknown parents → null.
 */
export function canvasUuidForItem(
  manifest: PublicShareManifest,
  itemUuid: string,
): string | null {
  if (isShareRootItem(manifest, itemUuid)) return null;
  const item = manifest.items.find((i) => i.uuid === itemUuid);
  if (!item) return null;
  const parent = item.parentUuid;
  if (parent == null || parent === '') return shareRootUuid(manifest);
  const root = shareRootUuid(manifest);
  if (parent !== root && !manifest.items.some((i) => i.uuid === parent)) {
    return null;
  }
  return parent;
}

export function pushShareCanvasVisit(
  stack: ShareVisit[],
  next: ShareVisit,
): ShareVisit[] {
  const cur = stack[stack.length - 1];
  if (cur && cur.canvasUuid === next.canvasUuid) {
    return [
      ...stack.slice(0, -1),
      {
        canvasUuid: cur.canvasUuid,
        focusItemUuid: next.focusItemUuid ?? cur.focusItemUuid,
      },
    ];
  }
  return [...stack, next];
}

export function withFocusOnCurrentVisit(
  stack: ShareVisit[],
  focusItemUuid: string | null | undefined,
): ShareVisit[] {
  if (stack.length === 0) return stack;
  const last = stack[stack.length - 1]!;
  return [...stack.slice(0, -1), { ...last, focusItemUuid }];
}

/** In-package original for a placeholder seat, or null when unlinked / root. */
export function resolvePlaceholderOriginal(
  manifest: PublicShareManifest,
  placeholder: SnapshotItem,
): SnapshotItem | null {
  if (placeholder.isPlaceholder !== true) return null;
  const uuid = placeholder.originalItemUuid?.trim();
  if (!uuid) return null;
  const original = manifest.items.find((i) => i.uuid === uuid);
  if (!original) return null;
  if (isShareRootItem(manifest, original.uuid)) return null;
  return original;
}
